import { format } from 'date-fns';
import { CalendarCheck, Lock } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TodoItemRow } from './TodoItemRow';
import type { TodoItem } from '@/lib/types';

interface TodayTodosProps {
  todos: TodoItem[];
  isLoading: boolean;
  onEdit: (todo: TodoItem) => void;
}

export function TodayTodos({ todos, isLoading, onEdit }: TodayTodosProps) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const todayTodos = todos.filter((t) => t.date === today);
  const completedCount = todayTodos.filter((t) => t.completed).length;
  const todayLabel = format(new Date(), 'EEEE, MMMM do');

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-card to-card/80 shadow-lg shadow-primary/5">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <CalendarCheck className="size-5" />
          </div>
          <div>
            <CardTitle className="text-lg">Today&apos;s To-Do List</CardTitle>
            <CardDescription className="flex items-center gap-1.5">
              <Lock className="size-3" />
              {todayLabel}
              {todayTodos.length > 0 && (
                <span className="text-primary ml-2">
                  {completedCount}/{todayTodos.length} done
                </span>
              )}
            </CardDescription>
          </div>
        </div>

        {todayTodos.length > 0 && (
          <div className="mt-3 h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
              style={{
                width: `${todayTodos.length > 0 ? (completedCount / todayTodos.length) * 100 : 0}%`,
              }}
            />
          </div>
        )}
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border/40">
                <Skeleton className="size-4 rounded" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : todayTodos.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 py-10 px-6 text-center">
            <p className="text-muted-foreground text-sm">
              No to-do items for today. Add one to get started!
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {todayTodos.map((todo) => (
              <TodoItemRow key={todo.id} todo={todo} onEdit={onEdit} compact />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
